import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Phone, MapPin, FileText, Lock, CurrencyEur, Trash, Warning,
  CheckCircle, Calendar, Clock,
} from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const formatNN = (n) => String(n).padStart(2, "0");

const isFutureDate = (iso) => {
  if (!iso) return false;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    return d.getTime() > today.getTime();
  } catch { return false; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
};

/**
 * Vista a pantalla completa de un encargo adicional (02, 03...).
 * Se muestra cuando el usuario selecciona el tab del encargo.
 */
export default function EncargoFullView({ exp, encargo, onReload, onChangeTab }) {
  // Estado local (sincronizado con props al cambiar)
  const [indicaciones, setIndicaciones] = useState(encargo.indicaciones || "");
  const [notas, setNotas] = useState(encargo.observaciones_especiales || "");
  const [reserva, setReserva] = useState(encargo.importe_reserva ?? "");
  const [tasacion, setTasacion] = useState(encargo.importe_tasacion ?? "");
  const [minuta, setMinuta] = useState(encargo.importe_minuta ?? "");
  const [rehuse, setRehuse] = useState(encargo.rehuse || "ninguno");
  const [rehuseMotivo, setRehuseMotivo] = useState(encargo.rehuse_motivo || "");
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);

  // Resync si cambia el encargo seleccionado
  useEffect(() => {
    setIndicaciones(encargo.indicaciones || "");
    setNotas(encargo.observaciones_especiales || "");
    setReserva(encargo.importe_reserva ?? "");
    setTasacion(encargo.importe_tasacion ?? "");
    setMinuta(encargo.importe_minuta ?? "");
    setRehuse(encargo.rehuse || "ninguno");
    setRehuseMotivo(encargo.rehuse_motivo || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encargo.id]);

  const terminado = encargo.estado === "terminado";
  const anulado = encargo.estado === "anulado";

  const persist = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/expedientes/${exp.id}/encargos/${encargo.id}`, patch);
      setFlash(true); setTimeout(() => setFlash(false), 1200);
      await onReload?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const markHito = async (key) => {
    const patch = { [key]: todayISO() };
    if (key === "fecha_visita" && !encargo.fecha_contacto) {
      patch.fecha_contacto = todayISO();
    }
    await persist(patch);
  };
  const clearHito = (key) => persist({ [key]: null });

  const remove = async () => {
    if (!confirm(`¿Eliminar el encargo ${formatNN(encargo.numero)}? Se perderán sus fechas e importes.`)) return;
    try {
      await api.delete(`/expedientes/${exp.id}/encargos/${encargo.id}`);
      await onReload?.();
      onChangeTab?.(1);  // Volver al encargo principal
    } catch (e) {
      alert(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const finalize = async () => {
    if (!confirm(`¿Finalizar el encargo ${formatNN(encargo.numero)}? Pasará a estado 'Terminado'.`)) return;
    await persist({
      fecha_cierre: todayISO(),
      importe_tasacion: tasacion === "" ? null : parseFloat(tasacion),
      importe_minuta: minuta === "" ? null : parseFloat(minuta),
      importe_reserva: reserva === "" ? null : parseFloat(reserva),
      rehuse,
      rehuse_motivo: rehuse === "ninguno" ? null : (rehuseMotivo.trim() || null),
      observaciones_especiales: notas,
    });
  };

  const saveIndicaciones = () => persist({ indicaciones });
  const saveNotas = () => persist({ observaciones_especiales: notas });
  const saveImporte = (field, raw) => persist({ [field]: raw === "" ? null : parseFloat(raw) });
  const saveRehuse = () => persist({ rehuse, rehuse_motivo: rehuse === "ninguno" ? null : (rehuseMotivo.trim() || null) });

  const visitaFuture = isFutureDate(encargo.fecha_visita);

  const stateBadge = terminado
    ? "bg-status-success-fg text-white"
    : anulado
      ? "bg-muted/40 text-muted"
      : "bg-brand text-white";

  return (
    <div className="flex-1 min-w-0 overflow-y-auto bg-surface" data-testid={`encargo-full-view-${encargo.numero}`}>
      {/* Cabecera del encargo */}
      <div className="bg-[#1E2332] text-white px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="font-display font-black text-[26px] tracking-tight">
            Encargo {formatNN(encargo.numero)}
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${stateBadge}`}>
            {encargo.estado}
          </span>
          <div className="ml-auto text-[10.5px] text-white/60 flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" weight="bold" />
              Creado {fmtDate(encargo.created_at)}
            </span>
            <span>·</span>
            <span>{encargo.created_by_name || "—"}</span>
            {flash && (
              <span className="ml-2 inline-flex items-center gap-1 text-status-success-bd">
                <CheckCircle className="w-3 h-3" weight="fill" /> guardado
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Indicaciones del encargo */}
      <div className="bg-white border-b border-border px-5 py-4">
        <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted">
          Indicaciones del encargo {formatNN(encargo.numero)}
        </label>
        <textarea
          rows={3}
          value={indicaciones}
          onChange={(e) => setIndicaciones(e.target.value)}
          onBlur={saveIndicaciones}
          data-testid={`encargo-${encargo.numero}-indicaciones`}
          placeholder="Qué hay que hacer en este encargo..."
          className="!text-[13px]"
        />
      </div>

      {/* Hitos CO / VIS / IP / IF (pantalla completa, idéntico estilo al principal) */}
      <div className="bg-white border-b border-border px-5 py-4">
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">
          Hitos del encargo
        </div>
        <div className="grid grid-cols-12 gap-3">
          {/* CO */}
          <div className="col-span-3" data-testid={`encargo-${encargo.numero}-co`}>
            <HitoLabel Icon={Phone} label="CO — Contacto" />
            <HitoBox done={!!encargo.fecha_contacto}>
              {encargo.fecha_contacto ? (
                <>
                  <div className="text-[12px] font-mono">{fmtDate(encargo.fecha_contacto)}</div>
                  <button onClick={() => clearHito("fecha_contacto")} className="text-[9px] uppercase tracking-wider text-muted hover:text-status-error-fg">Deshacer</button>
                </>
              ) : (
                <button onClick={() => markHito("fecha_contacto")} className="text-[10px] uppercase tracking-wider text-brand font-bold">Marcar hoy</button>
              )}
            </HitoBox>
          </div>

          {/* VISITA */}
          <div className="col-span-3" data-testid={`encargo-${encargo.numero}-vis`}>
            <HitoLabel Icon={MapPin} label="Visita">
              {encargo.fecha_visita && (
                <span className={`ml-1 normal-case tracking-normal text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                  visitaFuture ? "bg-sky-100 text-sky-800" : "bg-status-success-bg text-status-success-fg"
                }`}>
                  {visitaFuture ? "Programada" : "Realizada"}
                </span>
              )}
            </HitoLabel>
            <HitoBox done={!!encargo.fecha_visita} variant={visitaFuture ? "programada" : undefined}>
              {encargo.fecha_visita ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="text-[12px] font-mono">{fmtDate(encargo.fecha_visita)}</div>
                    <input
                      type="time"
                      value={encargo.hora_visita || ""}
                      onChange={(e) => persist({ hora_visita: e.target.value || null })}
                      className="!text-[10px] !py-0 !px-0 !border-0 !shadow-none !bg-transparent !h-5 !font-mono !text-muted w-[70px]"
                    />
                  </div>
                  <button onClick={() => { clearHito("fecha_visita"); persist({ hora_visita: null }); }} className="text-[9px] uppercase tracking-wider text-muted hover:text-status-error-fg">Deshacer</button>
                </>
              ) : (
                <button onClick={() => markHito("fecha_visita")} className="text-[10px] uppercase tracking-wider text-brand font-bold">Marcar hoy</button>
              )}
            </HitoBox>
          </div>

          {/* IP */}
          <div className="col-span-3" data-testid={`encargo-${encargo.numero}-ip`}>
            <HitoLabel Icon={FileText} label="IP — Preliminar" />
            <HitoBox done={!!encargo.fecha_preliminar}>
              {encargo.fecha_preliminar ? (
                <>
                  <div className="text-[12px] font-mono">{fmtDate(encargo.fecha_preliminar)}</div>
                  <button onClick={() => clearHito("fecha_preliminar")} className="text-[9px] uppercase tracking-wider text-muted hover:text-status-error-fg">Deshacer</button>
                </>
              ) : (
                <button onClick={() => markHito("fecha_preliminar")} className="text-[10px] uppercase tracking-wider text-brand font-bold">Marcar hoy</button>
              )}
            </HitoBox>
          </div>

          {/* IF */}
          <div className="col-span-3" data-testid={`encargo-${encargo.numero}-if`}>
            <HitoLabel Icon={Lock} label="IF — Final" />
            <HitoBox done={!!encargo.fecha_cierre} variant={encargo.fecha_cierre ? "done-final" : undefined}>
              {encargo.fecha_cierre ? (
                <>
                  <div className="text-[12px] font-mono">{fmtDate(encargo.fecha_cierre)}</div>
                  <span className="text-[9px] uppercase tracking-wider font-bold text-status-success-fg">Terminado</span>
                </>
              ) : (
                <button onClick={finalize} className="text-[10px] uppercase tracking-wider text-brand font-bold">Finalizar</button>
              )}
            </HitoBox>
          </div>
        </div>
      </div>

      {/* Importes */}
      <div className="bg-white border-b border-border px-5 py-4">
        <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">
          Importes del encargo
          <span className="normal-case tracking-normal text-[10px] italic text-muted/80 ml-2">
            · la minuta es opcional para encargos adicionales
          </span>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Importe label="Reserva" value={reserva} setValue={setReserva}
            onBlur={() => saveImporte("importe_reserva", reserva)}
            testid={`encargo-${encargo.numero}-reserva`} />
          <Importe label="Tasación" value={tasacion} setValue={setTasacion}
            onBlur={() => saveImporte("importe_tasacion", tasacion)}
            testid={`encargo-${encargo.numero}-tasacion`} />
          <Importe label="Minuta" value={minuta} setValue={setMinuta}
            onBlur={() => saveImporte("importe_minuta", minuta)}
            testid={`encargo-${encargo.numero}-minuta`}
            optional placeholder="Opcional" />
        </div>
      </div>

      {/* Notas de campo */}
      <div className="bg-white border-b border-border px-5 py-4">
        <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted">
          Notas de campo del encargo
        </label>
        <textarea
          rows={3}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          onBlur={saveNotas}
          data-testid={`encargo-${encargo.numero}-notas`}
          placeholder="Notas del perito tras la actuación de este encargo..."
          className="!text-[13px]"
        />
      </div>

      {/* Rehúse */}
      <div className="bg-white border-b border-border px-5 py-4">
        <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted !mb-1.5 block">
          Rehúse de este encargo
        </label>
        <div className="grid grid-cols-3 gap-2 max-w-md">
          {[
            { v: "ninguno", l: "Sin rehúse", tint: "neutral" },
            { v: "parcial", l: "Rehúse parcial", tint: "orange" },
            { v: "total",   l: "Rehúse total",   tint: "red" },
          ].map(({ v, l, tint }) => {
            const active = rehuse === v;
            const activeCls = {
              neutral: "bg-slate-800 border-slate-800 text-white",
              orange:  "bg-orange-500 border-orange-500 text-white",
              red:     "bg-red-600 border-red-600 text-white",
            }[tint];
            const idleCls = {
              neutral: "bg-white border-border text-muted hover:bg-surface",
              orange:  "bg-white border-orange-200 text-orange-700 hover:bg-orange-50",
              red:     "bg-white border-red-200 text-red-700 hover:bg-red-50",
            }[tint];
            return (
              <button
                key={v} type="button"
                onClick={() => { setRehuse(v); setTimeout(saveRehuse, 0); }}
                data-testid={`encargo-${encargo.numero}-rehuse-${v}`}
                className={`w-full text-[11.5px] uppercase tracking-wider font-semibold px-3 py-2 rounded-md border-2 transition-colors ${active ? activeCls : idleCls}`}
              >{l}</button>
            );
          })}
        </div>
        {rehuse !== "ninguno" && (
          <textarea
            rows={2}
            value={rehuseMotivo}
            onChange={(e) => setRehuseMotivo(e.target.value)}
            onBlur={saveRehuse}
            data-testid={`encargo-${encargo.numero}-rehuse-motivo`}
            placeholder="Motivo del rehúse (opcional pero recomendado)"
            className={`!text-[12px] mt-2 !bg-white max-w-2xl ${
              rehuse === "parcial" ? "!border-orange-300" : "!border-red-300"
            }`}
          />
        )}
      </div>

      {/* Acciones */}
      <div className="bg-white border-b border-border px-5 py-3 flex justify-between items-center">
        <button
          onClick={remove}
          data-testid={`encargo-${encargo.numero}-remove`}
          className="text-[11px] text-status-error-fg hover:underline inline-flex items-center gap-1"
        >
          <Trash className="w-3 h-3" weight="bold" /> Eliminar encargo
        </button>
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-muted normal-case tracking-normal">guardando...</span>}
          {!terminado && !anulado && (
            <button
              onClick={finalize}
              data-testid={`encargo-${encargo.numero}-finalize`}
              className="text-[11.5px] px-4 py-2 rounded-md bg-foreground text-white font-semibold hover:bg-foreground/90 inline-flex items-center gap-1.5"
            >
              <Lock className="w-3.5 h-3.5" weight="bold" />
              Finalizar encargo {formatNN(encargo.numero)}
            </button>
          )}
        </div>
      </div>

      <div className="h-6" />
    </div>
  );
}

// ---- Sub-componentes ----

function HitoLabel({ Icon, label, children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest font-bold text-muted mb-1 flex items-center gap-1">
      <Icon className="w-3 h-3" weight="bold" /> {label}
      {children}
    </div>
  );
}

function HitoBox({ done, variant, children }) {
  const cls = !done
    ? "bg-surface border-border"
    : variant === "programada"
      ? "bg-sky-50 border-sky-300 text-sky-800"
      : variant === "done-final"
        ? "bg-status-success-bg border-status-success-bd ring-1 ring-status-success-bd/40"
        : "bg-status-success-bg/40 border-status-success-bd";
  return <div className={`rounded-md border p-2 transition-all ${cls}`}>{children}</div>;
}

function Importe({ label, value, setValue, onBlur, testid, optional, placeholder }) {
  return (
    <div>
      <label className="!text-[10px] !uppercase !tracking-widest !font-semibold !text-muted flex items-center gap-1">
        <CurrencyEur className="w-3 h-3" weight="bold" /> {label}
        {optional && <span className="normal-case tracking-normal text-[9px] italic">· opcional</span>}
      </label>
      <input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        data-testid={testid}
        placeholder={placeholder || "0,00"}
        className="!text-[13px] !py-1.5 !px-2 !font-mono"
      />
    </div>
  );
}
